<template>
  <section class="space-applications" aria-live="polite">
    <div class="applications-toolbar">
      <div>
        <strong>{{ spaceName || "当前空间" }}</strong>
        <span>{{ applications.length }} 个应用</span>
      </div>
      <el-button
        :loading="loading"
        icon="el-icon-refresh"
        size="small"
        @click="loadApplications"
      >刷新</el-button>
    </div>

    <div v-loading="loading" class="application-list">
      <el-empty
        v-if="!loading && !applications.length"
        :image-size="72"
        description="当前空间没有已安装应用"
      />
      <article
        v-for="installation in applications"
        :key="installation.id"
        class="application-row"
      >
        <div class="application-heading">
          <img
            v-if="applicationIcon(installation)"
            :alt="applicationName(installation)"
            :src="applicationIcon(installation)"
            class="application-icon"
          >
          <span v-else class="application-icon application-icon--fallback" aria-hidden="true">
            {{ applicationInitial(installation) }}
          </span>
          <div class="application-identity">
            <div>
              <strong>{{ applicationName(installation) }}</strong>
              <el-tag :type="statusTagType(installation.status)" size="mini">
                {{ statusLabel(installation.status) }}
              </el-tag>
            </div>
            <span>{{ applicationCode(installation) }} · {{ environmentLabel(installation) }}</span>
          </div>
          <div v-if="installation.status !== 'uninstalled'" class="application-actions">
            <el-button
              v-if="installation.status === 'active'"
              :loading="actionID === installation.id"
              icon="el-icon-video-pause"
              size="mini"
              @click="handleStatusChange(installation, 'suspended')"
            >暂停</el-button>
            <el-button
              v-if="installation.status === 'suspended'"
              :loading="actionID === installation.id"
              icon="el-icon-video-play"
              size="mini"
              type="primary"
              @click="handleStatusChange(installation, 'active')"
            >恢复</el-button>
            <el-button
              :loading="actionID === installation.id"
              icon="el-icon-delete"
              size="mini"
              type="danger"
              plain
              @click="handleUninstall(installation)"
            >卸载</el-button>
          </div>
        </div>

        <div class="application-details">
          <div class="application-detail application-detail--scopes">
            <span class="detail-label">授权范围</span>
            <div class="scope-list">
              <el-tag
                v-for="scope in scopes(installation)"
                :key="scope"
                effect="plain"
                size="mini"
                type="info"
              >{{ scope }}</el-tag>
              <span v-if="!scopes(installation).length" class="detail-empty">-</span>
            </div>
          </div>
          <div class="application-detail">
            <span class="detail-label">Credential</span>
            <div class="credential-statuses">
              <el-tag
                v-for="status in credentialStatuses(installation)"
                :key="status.value"
                :type="credentialTagType(status.value)"
                effect="plain"
                size="mini"
              >{{ credentialStatusLabel(status.value) }} {{ status.count }}</el-tag>
              <span v-if="!credentialStatuses(installation).length" class="detail-empty">-</span>
            </div>
          </div>
        </div>

        <div class="usage-grid">
          <div>
            <span>AI 任务</span>
            <strong>{{ numberValue(installation.usage && installation.usage.ai_gateway_runs) }}</strong>
          </div>
          <div>
            <span>应用用量</span>
            <strong>{{ numberValue(installation.usage && installation.usage.application_credit_usages) }}</strong>
          </div>
          <div>
            <span>AI 积分</span>
            <strong>{{ numberValue(installation.usage && installation.usage.ai_gateway_consumed_credits) }}</strong>
          </div>
          <div>
            <span>应用积分</span>
            <strong>{{ numberValue(installation.usage && installation.usage.application_consumed_credits) }}</strong>
          </div>
          <div>
            <span>累计积分</span>
            <strong>{{ numberValue(installation.usage && installation.usage.total_consumed_credits) }}</strong>
          </div>
          <div>
            <span>最近使用</span>
            <strong class="usage-time">{{ formatDate(installation.usage && installation.usage.last_used_at) }}</strong>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import {
  changeInstallationStatus,
  loadSpaceApplications,
} from "@/services/spaceApplications";

export default {
  name: "SpaceApplicationsPanel",
  props: {
    spaceId: {
      type: String,
      required: true,
    },
    spaceName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      applications: [],
      loading: false,
      actionID: "",
    };
  },
  watch: {
    spaceId(next, previous) {
      if (next && next !== previous) {
        this.loadApplications();
      }
    },
  },
  mounted() {
    this.loadApplications();
  },
  methods: {
    async loadApplications() {
      if (!this.spaceId || this.loading) return;
      this.loading = true;
      try {
        this.applications = await loadSpaceApplications(this.spaceId);
      } catch (error) {
        this.applications = [];
        this.$message.error(error.message || "加载已安装应用失败");
      } finally {
        this.loading = false;
      }
    },
    async handleStatusChange(installation, status) {
      if (!installation || this.actionID) return;
      this.actionID = installation.id;
      try {
        const updated = await changeInstallationStatus(installation.id, status);
        this.replaceInstallation(installation, updated || { status });
        this.$message.success(status === "active" ? "应用已恢复" : "应用已暂停");
      } catch (error) {
        this.$message.error(error.message || "更新应用状态失败");
      } finally {
        this.actionID = "";
      }
    },
    async handleUninstall(installation) {
      if (!installation || this.actionID) return;
      try {
        await this.$confirm(
          `确认卸载“${this.applicationName(installation)}”？`,
          "卸载应用",
          { type: "warning", confirmButtonText: "确认卸载", cancelButtonText: "取消" },
        );
      } catch (error) {
        return;
      }
      this.actionID = installation.id;
      try {
        const updated = await changeInstallationStatus(installation.id, "uninstalled");
        this.replaceInstallation(installation, updated || { status: "uninstalled" });
        this.$message.success("应用已卸载");
      } catch (error) {
        this.$message.error(error.message || "卸载应用失败");
      } finally {
        this.actionID = "";
      }
    },
    replaceInstallation(current, updated) {
      const index = this.applications.findIndex((item) => item.id === current.id);
      if (index < 0) return;
      this.$set(this.applications, index, {
        ...current,
        ...updated,
        application: current.application,
        usage: current.usage,
        credentials: current.credentials,
      });
    },
    applicationName(installation) {
      const application = installation && installation.application ? installation.application : {};
      return application.name || application.code || "未命名应用";
    },
    applicationCode(installation) {
      const application = installation && installation.application ? installation.application : {};
      return application.code || installation.application_id || "-";
    },
    applicationIcon(installation) {
      const application = installation && installation.application ? installation.application : {};
      return String(application.icon_url || "").trim();
    },
    applicationInitial(installation) {
      return this.applicationName(installation).slice(0, 1).toUpperCase();
    },
    environmentLabel(installation) {
      const application = installation && installation.application ? installation.application : {};
      const labels = { development: "开发", test: "测试", production: "生产" };
      return labels[application.environment] || application.environment || "生产";
    },
    scopes(installation) {
      return Array.isArray(installation && installation.consented_scopes)
        ? installation.consented_scopes
        : [];
    },
    credentialStatuses(installation) {
      const counts = {};
      const credentials = Array.isArray(installation && installation.credentials)
        ? installation.credentials
        : [];
      credentials.forEach((credential) => {
        const status = String(credential && credential.status || "unknown");
        counts[status] = (counts[status] || 0) + 1;
      });
      return Object.keys(counts).sort().map((value) => ({ value, count: counts[value] }));
    },
    statusLabel(status) {
      return { active: "运行中", suspended: "已暂停", uninstalled: "已卸载" }[status] || status || "未知";
    },
    statusTagType(status) {
      return { active: "success", suspended: "warning", uninstalled: "info" }[status] || "info";
    },
    credentialStatusLabel(status) {
      return { active: "有效", pending: "待激活", revoked: "已撤销" }[status] || "未知";
    },
    credentialTagType(status) {
      return { active: "success", pending: "warning", revoked: "info" }[status] || "info";
    },
    numberValue(value) {
      const parsed = Number(value || 0);
      return Number.isFinite(parsed) ? parsed.toLocaleString("zh-CN") : "0";
    },
    formatDate(value) {
      if (!value) return "-";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "-";
      return parsed.toLocaleString("zh-CN", { hour12: false });
    },
  },
};
</script>

<style lang="scss" scoped>
.space-applications {
  min-height: 220px;
}

.applications-toolbar,
.application-heading,
.applications-toolbar > div,
.application-identity > div,
.application-actions,
.scope-list,
.credential-statuses {
  display: flex;
  align-items: center;
}

.applications-toolbar {
  min-height: 52px;
  justify-content: space-between;
  gap: 16px;

  > div {
    min-width: 0;
    gap: 10px;
  }

  strong {
    overflow: hidden;
    color: #2a303a;
    font-size: 15px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    color: #8a929d;
    font-size: 13px;
  }
}

.application-list {
  min-height: 168px;
}

.application-row {
  padding: 16px 12px;
  border-bottom: 1px solid #edf0f3;
}

.application-heading {
  gap: 12px;
}

.application-icon {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 6px;
  object-fit: cover;
}

.application-icon--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #313845;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.application-identity {
  min-width: 0;
  flex: 1 1 auto;

  > div {
    min-width: 0;
    gap: 7px;
  }

  strong {
    overflow: hidden;
    color: #2a303a;
    font-size: 15px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > span {
    display: block;
    margin-top: 5px;
    overflow: hidden;
    color: #8a929d;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.application-actions {
  flex: 0 0 auto;
  gap: 7px;
}

.application-details {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(180px, 0.6fr);
  gap: 14px;
  margin: 14px 0;
}

.application-detail {
  min-width: 0;
}

.detail-label {
  display: block;
  margin-bottom: 7px;
  color: #7c8591;
  font-size: 12px;
}

.scope-list,
.credential-statuses {
  min-height: 24px;
  flex-wrap: wrap;
  gap: 6px;
}

.detail-empty {
  color: #a1a8b1;
  font-size: 13px;
}

.usage-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  border: 1px solid #e7eaee;
  border-radius: 6px;
  background: #e7eaee;

  > div {
    min-width: 0;
    padding: 10px;
    background: #f8f9fb;
  }

  span,
  strong {
    display: block;
  }

  span {
    color: #7c8591;
    font-size: 11px;
  }

  strong {
    margin-top: 5px;
    overflow: hidden;
    color: #2a303a;
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .usage-time {
    font-size: 12px;
  }
}

@media (max-width: 767px) {
  .application-heading {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .application-identity {
    min-width: calc(100% - 54px);
  }

  .application-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .application-details {
    grid-template-columns: minmax(0, 1fr);
  }

  .usage-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
